import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsersStore } from '@/stores/users';
import { ERouterNames, EUserRoles } from '@/types';

type TProps = {
  components: Partial<Record<EUserRoles, ReactNode>>;
  withoutNavigate?: boolean;
};

/**
 * Conditionally renders a component based on the user's role.
 * If no component matches the user's role and `withoutNavigate` is false, the user is redirected to a default route.
 * @param props - Component props.
 * @param props.components - A partial mapping of user roles to React nodes.
 * @param props.withoutNavigate - If true, disables automatic navigation when no matching component is found.
 * @returns The React node corresponding to the user's role, or null if none.
 */
export const RoleControl = ({
  components,
  withoutNavigate = false,
}: TProps) => {
  const { role } = useUsersStore();
  const component = role ? components[role] : null;
  const navigate = useNavigate();
  useEffect(() => {
    if (!role) return;
    if (!withoutNavigate && !component) {
      navigate(ERouterNames.ROUTE_ME);
    }
  }, [component, navigate, role, withoutNavigate]);

  if (!role) return null;

  return component;
};
